/**
 * A payback's match chip words (TMMAPP-0121 item 3), shared by the review queue's pair card and the
 * drawer's reimbursement section (TMMAPP-0122 item 12; the chip itself is `MatchPill`). The words
 * come from the served grade, `reasonCode` and `fractionDenominator`, never from server prose and
 * never from a computed share (TMM-B3).
 */
import type { TFunction } from 'i18next';
import type { ReimbursementGrade, ReimbursementReasonCode } from '@/api/reimbursements';

export function gradeChipColor(grade: ReimbursementGrade): 'success' | 'info' | 'warning' {
  if (grade === 'EXACT') return 'success';
  if (grade === 'FRACTION') return 'info';
  return 'warning';
}

/** A clean share's words; any other n is "Part of it". */
const FRACTION_KEYS: Readonly<Record<number, string>> = {
  2: 'reimb.match.half',
  3: 'reimb.match.third',
  4: 'reimb.match.quarter',
  5: 'reimb.match.fifth',
  6: 'reimb.match.sixth',
};

/** What the chip reads from: a link carries all three; a bare candidate only its grade. */
export interface MatchFacts {
  grade: ReimbursementGrade;
  reasonCode: ReimbursementReasonCode | null;
  fractionDenominator: number | null;
}

/**
 * The chip's words. A link the person made reads "Linked by you"; a FRACTION names the served
 * `fractionDenominator`; NEAR names the difference, which the caller formats from its amounts.
 */
export function matchWords(t: TFunction, match: MatchFacts, nearDifference: () => string): string {
  if (match.reasonCode === 'MANUAL') return t('reimb.match.manual');
  if (match.grade === 'EXACT') return t('reimb.match.exact');
  if (match.grade === 'FRACTION') {
    const n = match.fractionDenominator;
    return t((n !== null ? FRACTION_KEYS[n] : undefined) ?? 'reimb.match.part');
  }
  return t('reimb.match.near', { difference: nearDifference() });
}
